import React from 'react';
import EntityLinkButton from '../../../shared/components/linked-entities/EntityLinkButton';
import { useLinkedEntityCards } from '../../../shared/components/linked-entities/LinkedEntityCardHost';
import { entityLabel, selectedEntityIds } from '../../../shared/utils/entitySelection';
import { formatCalendarDate, formatEventOwner, formatTimeLabel } from '../utils/calendar.helpers';
import EventOriginLink, { formatEventOrigin } from './EventOriginLink';

const DETAIL_TITLES = {
  meeting: 'Dettagli evento', deadline: 'Dettagli scadenza', internal: 'Dettagli riunione',
  training: 'Dettagli promemoria', incasso: 'Dettagli incasso', pagamento: 'Dettagli pagamento',
};
const PARTY_LABELS = { staff: 'Personale', client: 'Cliente', supplier: 'Fornitore' };
const MODULE_LABELS = { clients: 'Clienti', suppliers: 'Fornitori', staff: 'Team', deals: 'Affari', documents: 'Documenti', accounting: 'Contabilità' };

export function getEventDetailType(event) {
  if (event.financialDetails?.kind === 'expense') return 'pagamento';
  if (event.financialDetails?.kind === 'payment') return 'incasso';
  return ({ riunione: 'internal', reminder: 'training', promemoria: 'training', scadenza: 'deadline', income: 'incasso', payment: 'pagamento', expense: 'pagamento' })[event.type] || event.type || 'meeting';
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const amount = Number(value);
  return Number.isFinite(amount) ? new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount) : String(value);
}

function Field({ label, wide = false, children }) {
  return <div className={`calendar-event-detail__field${wide ? ' calendar-event-detail__field--wide' : ''}`}>
    <dt>{label}</dt><dd className="select-text">{children || '—'}</dd>
  </div>;
}

export function EventDetailSection({ title, children, className = '' }) {
  return <section aria-label={title} className={`calendar-event-detail__section ${className}`.trim()}>
    <h3>{title}</h3>{children}
  </section>;
}

// Calendar attendees identify staff records; legacy names remain readable even
// when no unique staff record can be resolved.
function partyEntries(event, kind, catalog) {
  const explicitIds = selectedEntityIds(event, kind);
  const financial = event.financialDetails;
  if (!explicitIds.length && financial?.counterpartyType === kind && financial.counterpartyId != null) {
    explicitIds.push(String(financial.counterpartyId));
  }
  const fallback = event[`${kind}Name`] || (financial?.counterpartyType === kind ? financial.counterpartyName : '') || '';
  const labels = fallback.split(';').map(label => label.trim()).filter(Boolean);
  const entries = explicitIds.map((id, index) => ({
    id, label: entityLabel(catalog.find(item => String(item.id) === id)) ||
      (labels.length === explicitIds.length ? labels[index] : explicitIds.length === 1 ? fallback : '') || `${PARTY_LABELS[kind]} ${id}`,
  }));
  if (kind === 'staff') {
    (event.attendees || event.attendeeIds || []).forEach(value => {
      const name = String(value).trim();
      const matches = catalog.filter(item => entityLabel(item).toLocaleLowerCase('it') === name.toLocaleLowerCase('it'));
      const member = catalog.find(item => String(item.id) === name) || (matches.length === 1 ? matches[0] : null);
      const id = member ? String(member.id) : /^\d+$/.test(name) ? name : null;
      if (!entries.some(entry => id ? entry.id === id : entry.label === name)) {
        entries.push({ id, label: member ? entityLabel(member) : id ? `Personale ${id}` : name });
      }
    });
  }
  if (!entries.length && fallback) entries.push({ label: fallback });
  return entries;
}

export default function EventDetailContent({ event, staff = [], clients = [], suppliers = [], enabledModuleKeys }) {
  const { openEntityCard } = useLinkedEntityCards();
  const type = getEventDetailType(event);
  const financial = event.financialDetails;
  const isFinancial = ['incasso', 'pagamento'].includes(type);
  const description = event.description || event.notes || 'Nessuna descrizione disponibile per questo evento.';
  const groups = { staff: partyEntries(event, 'staff', staff), client: partyEntries(event, 'client', clients), supplier: partyEntries(event, 'supplier', suppliers) };
  const detailGroups = type === 'incasso' ? ['client'] : type === 'pagamento' ? ['staff', 'supplier'] : [];
  const participantGroups = ['staff', 'client', 'supplier'].filter(kind => !detailGroups.includes(kind) && (!isFinancial || groups[kind].length));
  const duration = ({ '30m': '30 minuti', '1h': '1 ora', '2h': '2 ore', '4h': '4 ore' })[event.duration] || event.duration || '—';
  const owner = event.ownerName || event.managedBy || event.createdBy;
  const hasOrigin = Boolean(event.linkedEntity || event.sourceLabel);
  const showExtra = owner || (type !== 'deadline' && hasOrigin) || (type === 'meeting' && event.endTime);
  const originModule = event.linkedEntity?.moduleKey;
  const originUnavailable = Array.isArray(enabledModuleKeys) && originModule && !enabledModuleKeys.includes(originModule);
  const origin = originUnavailable ? <span>{formatEventOrigin(event)}<span role="alert" className="calendar-event-detail__unavailable">Modulo {MODULE_LABELS[originModule] || originModule} non disponibile.</span></span> : <EventOriginLink event={event} />;
  const partyLinks = kind => <span className="calendar-event-detail__party-links">
    {groups[kind].length ? groups[kind].map((entry, index) => entry.id != null
      ? <EntityLinkButton key={`${entry.id}:${index}`} onClick={() => openEntityCard({ entityType: kind, entityId: entry.id })}>{entry.label}</EntityLinkButton>
      : <span key={index}>{entry.label}</span>) : '—'}
  </span>;

  return <>
    <EventDetailSection title="Descrizione">
      <p className="calendar-event-detail__description select-text">{description}</p>
    </EventDetailSection>

    <EventDetailSection title={DETAIL_TITLES[type] || 'Dettagli evento'}>
      <dl className="calendar-event-detail__fields">
        <Field label="Data">{formatCalendarDate(event.date)}</Field>
        {type === 'meeting' ? <>
          <Field label="Data fine">{event.endDate ? formatCalendarDate(event.endDate) : '—'}</Field>
          <Field label="Ora">{formatTimeLabel(event.time)}</Field>
          <Field label="Durata">{duration}</Field>
          <Field label="Luogo" wide>{event.location}</Field>
        </> : type === 'deadline' ? <>
          <Field label="Origine">{origin}</Field>
          {event.time ? <Field label="Ora">{formatTimeLabel(event.time)}</Field> : null}
        </> : isFinancial ? <>
          <Field label="Importo">{formatMoney(financial?.amount ?? event.amount)}</Field>
          {detailGroups.map(kind => <Field key={kind} label={PARTY_LABELS[kind]} wide={type === 'incasso'}>{partyLinks(kind)}</Field>)}
        </> : <>
          <Field label="Ora">{formatTimeLabel(event.time)}</Field>
          <Field label="Durata">{duration}</Field>
          <Field label="Luogo">{event.location}</Field>
        </>}
      </dl>
    </EventDetailSection>

    {participantGroups.length ? <EventDetailSection title="Partecipanti">
      <dl className="calendar-event-detail__participants">
        {participantGroups.map(kind => <Field key={kind} label={PARTY_LABELS[kind]}>{partyLinks(kind)}</Field>)}
      </dl>
    </EventDetailSection> : null}

    {showExtra ? <EventDetailSection title="Informazioni aggiuntive">
      <dl className="calendar-event-detail__fields">
        {type !== 'deadline' && hasOrigin ? <Field label="Origine">{origin}</Field> : null}
        {owner ? <Field label="Gestione">{formatEventOwner(event)}</Field> : null}
        {type === 'meeting' && event.endTime ? <Field label="Ora fine">{formatTimeLabel(event.endTime)}</Field> : null}
      </dl>
    </EventDetailSection> : null}

    {financial ? <EventDetailSection title={financial.kind === 'expense' ? 'Uscita collegata' : 'Pagamento collegato'}>
      <dl className="calendar-event-detail__fields">
        <Field label={financial.counterpartyLabel || 'Beneficiario / Destinatario'}>{financial.counterpartyName || financial.beneficiary || financial.recipient}</Field>
        <Field label="Riferimento">{financial.reference}</Field>
        <Field label="Stato pagamento">{financial.status}</Field>
      </dl>
    </EventDetailSection> : null}
    {event.description && event.notes && event.notes !== event.description ? <EventDetailSection title="Note">
      <p className="calendar-event-detail__description select-text">{event.notes}</p>
    </EventDetailSection> : null}
  </>;
}
